import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';
import { PrismButton, PrismComboSelectInput } from '@mylighthouse/prism-ember';
import EmberTooltip from '@mylighthouse/prism-ember/components/ember-tooltip';
import type { Option } from '@mylighthouse/prism-ember/types/components/prism/combo-select-input';
import { t } from 'ember-intl';
import { or } from 'ember-truth-helpers';
import { formatUrl } from 'frontend-utils/utils/url/format-url';
import { openUrl } from 'frontend-utils/utils/url/open-url';
import type AccountSelectorSubscription from 'lumen/models/account-selector-subscription';
import type AjaxService from 'lumen/services/ajax';
import { SUBSCRIPTION_TYPE_TRANSLATION_KEYS } from 'lumen/utils/constants/general-constants';

interface SubscriptionOption extends Option {
  subscriptionId: string;
}

interface SubscriptionTooltipInfo {
  deniedFeatures: string[];
  id: string;
  name: string;
  restrictedFeatures: string[];
}

interface HotelAndMarketStaffAccessIndicationSignature {
  Args: {};
}

export default class HotelAndMarketStaffAccessIndication extends Component<HotelAndMarketStaffAccessIndicationSignature> {
  @service declare access: Services['access'];
  @service declare ajax: AjaxService;
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare intl: Services['intl'];
  @service declare navigation: Services['navigation'];

  @tracked isDropdownOpen = false;

  value: SubscriptionOption = {
    icon: 'lock',
    label: '',
    subscriptionId: '',
  };

  get accessDeniedFeatures(): string[] {
    return this.stripPrefix(this.access.deniedFeatures, 'denied.');
  }

  get accessRestrictedFeatures(): string[] {
    return this.stripPrefix(this.access.restrictedFeatures, 'restricted.');
  }

  get isSearchable(): boolean {
    return this.options.length > 5;
  }

  get options(): SubscriptionOption[] {
    return this.navigation.accountSelectorSubscriptions.map(
      (subscription: AccountSelectorSubscription) => ({
        label: this.intl.t(
          SUBSCRIPTION_TYPE_TRANSLATION_KEYS[subscription.type],
        ),
        unsafe_subLabel: subscription.id ?? '',
        subscriptionId: subscription.id!,
      }),
    );
  }

  @cached get tooltipSubscriptions(): SubscriptionTooltipInfo[] {
    return this.navigation.accountSelectorSubscriptions
      .map(({ id, type }: AccountSelectorSubscription) => {
        const features = [...this.navigation.getFeatures(id!)];

        return {
          id: id!,
          name: this.intl.t(SUBSCRIPTION_TYPE_TRANSLATION_KEYS[type]),
          restrictedFeatures: this.stripPrefix(features, 'restricted.'),
          deniedFeatures: this.stripPrefix(features, 'denied.'),
        };
      })
      .filter(
        ({ restrictedFeatures, deniedFeatures }) =>
          restrictedFeatures.length > 0 || deniedFeatures.length > 0,
      );
  }

  private openAccessPage(params: Record<string, string>): void {
    const url = formatUrl(
      `${this.ajax.host}/admin/user_permissions/temporary_feature_flag_request/`,
      params,
    );

    openUrl(url, true);
  }

  searchMatcher(option: SubscriptionOption, searchText: string): number {
    const term = searchText.toLowerCase();
    return option.label.toLowerCase().includes(term) ||
      option.subscriptionId.includes(term)
      ? 1
      : -1;
  }

  private stripPrefix(features: string[], prefix: string): string[] {
    return features
      .filter(
        (feature) =>
          feature.startsWith(prefix) &&
          feature !== `${prefix}user.staff.context`,
      )
      .map((feature) => feature.slice(prefix.length));
  }

  @action handleChange(option: SubscriptionOption): void {
    this.openAccessPage({
      subscription_id: option.subscriptionId,
      user_id: this.hotelAndMarketService.user.id!,
    });
  }

  @action onDropdownClose(): void {
    this.isDropdownOpen = false;
  }

  @action onDropdownOpen(): void {
    this.isDropdownOpen = true;
  }

  @action openDirectAccessPage(): void {
    if (this.access.hasDeniedFeaturesOnly) {
      return;
    }

    const { market, user } = this.hotelAndMarketService;
    const hotelOrMarketKey = market ? 'market_id' : 'subscription_id';

    this.openAccessPage({
      [hotelOrMarketKey]: this.hotelAndMarketService.hotelOrMarket.id!,
      user_id: user.id!,
    });
  }

  <template>
    {{! template-lint-disable no-bare-strings }}
    <div class="flex items-center">
      {{#if
        (or
          this.hotelAndMarketService.market this.navigation.isSingleSubscription
        )
      }}
        <PrismButton
          @buttonType={{if
            this.access.hasDeniedFeaturesOnly
            "critical"
            "warning"
          }}
          @icon="lock"
          @onClick={{this.openDirectAccessPage}}
          @size="medium"
          class={{if
            this.access.hasDeniedFeaturesOnly
            "staff-access-indication--disabled"
          }}
          data-test-id="staff-access-indication"
        >
          <EmberTooltip @tooltipClass="info-tooltip staff-access-tooltip">
            {{#if this.accessRestrictedFeatures.length}}
              <p class="text-300--medium pb-100">Restricted features</p>
              {{#each this.accessRestrictedFeatures as |feature|}}
                <p class="text-200--regular">{{t feature}}</p>
              {{/each}}
            {{/if}}

            {{#if this.accessDeniedFeatures.length}}
              <p
                class="text-300--medium pb-100
                  {{if this.accessRestrictedFeatures.length 'pt-100'}}"
              >Denied features</p>
              {{#each this.accessDeniedFeatures as |feature|}}
                <p class="text-200--regular">{{t feature}}</p>
              {{/each}}
            {{/if}}
          </EmberTooltip>
        </PrismButton>
      {{else}}
        <PrismComboSelectInput
          @isSearchable={{if this.isSearchable true}}
          @onChange={{this.handleChange}}
          @onClose={{this.onDropdownClose}}
          @onOpen={{this.onDropdownOpen}}
          @options={{this.options}}
          @searchMatcher={{if this.isSearchable this.searchMatcher}}
          @unsafe_renderInPlace={{true}}
          @value={{this.value}}
          data-test-id="staff-access-indication"
        />
        {{#unless this.isDropdownOpen}}
          <EmberTooltip @tooltipClass="info-tooltip staff-access-tooltip">
            {{#each this.tooltipSubscriptions as |subscription index|}}
              <div class={{if index "mt-200 border-t pt-200"}}>
                <p class="text-300--medium pb-100">{{subscription.name}}
                  -
                  <span class="text-200--regular">{{subscription.id}}</span></p>

                {{#if subscription.restrictedFeatures.length}}
                  <p class="text-200--medium pb-100">Restricted features</p>
                  {{#each subscription.restrictedFeatures as |feature|}}
                    <p class="text-200--regular pl-100">{{t feature}}</p>
                  {{/each}}
                {{/if}}

                {{#if subscription.deniedFeatures.length}}
                  <p
                    class="text-200--regular pb-50
                      {{if subscription.restrictedFeatures.length 'pt-100'}}"
                  >Denied features</p>
                  {{#each subscription.deniedFeatures as |feature|}}
                    <p class="text-200--regular pl-100">{{t feature}}</p>
                  {{/each}}
                {{/if}}
              </div>
            {{/each}}
          </EmberTooltip>
        {{/unless}}
      {{/if}}
    </div>
  </template>
}
